import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CLIENT_ROLE_OPTIONS } from "@/lib/workspace";
import { ACCEPT } from "@/components/workspace/WorkItemParts";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

function AudiencePicker({ orgId, type, values, onType, onValues }) {
  const [people, setPeople] = useState([]);
  useEffect(() => { if (type === "users") api.get(`/workspace/people?org_id=${orgId}`).then((r) => setPeople(r.data)).catch(() => {}); }, [type, orgId]);
  const toggle = (v) => onValues(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <div>
      <Label>Who should see it?</Label>
      <Select value={type} onValueChange={(t) => { onType(t); onValues([]); }}>
        <SelectTrigger className="mt-1" data-testid="audience-type"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Everyone in the company</SelectItem>
          <SelectItem value="roles">Selected roles</SelectItem>
          <SelectItem value="departments">Selected departments</SelectItem>
          <SelectItem value="users">Named people</SelectItem>
        </SelectContent>
      </Select>
      {type === "roles" && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {CLIENT_ROLE_OPTIONS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => toggle(k)} data-testid={`aud-role-${k}`}
              className={`rounded-full border px-2.5 py-1 text-xs ${values.includes(k) ? "border-[#FFB000] bg-[#FFB000]/10 text-[#0B1729]" : "border-[#E4E8EF] text-[#1E2D44]/60"}`}>{l}</button>
          ))}
        </div>
      )}
      {type === "departments" && (
        <Input className="mt-2" placeholder="e.g. Training, Fire Safety" value={values.join(", ")} data-testid="aud-departments"
          onChange={(e) => onValues(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} />
      )}
      {type === "users" && (
        <div className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-[#E4E8EF] p-2">
          {people.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={values.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`aud-user-${p.id}`} /> {p.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function ResourceFormDialog({ orgId, mode, res, onClose, onSaved }) {
  const isNew = mode === "create";
  const [f, setF] = useState({ title: "", kind: res?.kind || "article", summary: "", url: res?.url || "", body: res?.body || "",
    audience_type: "all", audience_values: [], requires_acknowledgement: false, change_note: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (file && file.size > 25 * 1024 * 1024) return toast.error("Files must be 25 MB or smaller.");
    const fd = new FormData();
    if (isNew) {
      ["title", "kind", "summary", "audience_type"].forEach((k) => fd.append(k, f[k]));
      fd.append("audience_values", JSON.stringify(f.audience_values));
      fd.append("requires_acknowledgement", String(f.requires_acknowledgement));
    } else fd.append("change_note", f.change_note);
    if (f.kind === "link") fd.append("url", f.url);
    if (f.kind === "article") fd.append("body", f.body);
    if (file) fd.append("file", file);
    setSaving(true);
    try {
      await api.post(isNew ? `/workspace/resources?org_id=${orgId}` : `/workspace/resources/${res.id}/versions`, fd);
      toast.success(isNew ? "Resource published" : "New version published — acknowledgements reset");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="resource-form-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Publish a company resource" : `New version of ${res.title}`}</DialogTitle>
          <DialogDescription>{isNew ? "Documents, links or short articles for your team." : "Everyone who must acknowledge it will be asked again."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          {isNew && <>
            <div><Label>Title</Label><Input value={f.title} onChange={(e) => set("title")(e.target.value)} className="mt-1" data-testid="resource-title" /></div>
            <div><Label>Type</Label>
              <Select value={f.kind} onValueChange={set("kind")}>
                <SelectTrigger className="mt-1" data-testid="resource-kind"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="article">Short article</SelectItem><SelectItem value="document">Document (file)</SelectItem><SelectItem value="link">Link</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Summary</Label><Input value={f.summary} onChange={(e) => set("summary")(e.target.value)} className="mt-1" data-testid="resource-summary" /></div>
          </>}
          {!isNew && <div><Label>What changed?</Label><Input value={f.change_note} onChange={(e) => set("change_note")(e.target.value)} className="mt-1" data-testid="resource-change-note" /></div>}
          {f.kind === "article" && <div><Label>Article</Label><Textarea rows={6} value={f.body} onChange={(e) => set("body")(e.target.value)} className="mt-1" data-testid="resource-body" /></div>}
          {f.kind === "link" && <div><Label>Web address</Label><Input value={f.url} onChange={(e) => set("url")(e.target.value)} className="mt-1" placeholder="https://" data-testid="resource-url" /></div>}
          {f.kind === "document" && (
            <div><Label>File (PDF, image or Office, max 25 MB)</Label>
              <input type="file" accept={ACCEPT} className="mt-1 block w-full text-sm" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="resource-file" />
            </div>
          )}
          {isNew && <>
            <AudiencePicker orgId={orgId} type={f.audience_type} values={f.audience_values} onType={set("audience_type")} onValues={set("audience_values")} />
            <label className="flex items-center justify-between rounded-lg border border-[#E4E8EF] px-3 py-2.5 text-sm">
              Require people to acknowledge they've read it
              <Switch checked={f.requires_acknowledgement} onCheckedChange={set("requires_acknowledgement")} data-testid="resource-requires-ack" />
            </label>
          </>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-resource-btn">
            {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AckReportDialog({ res, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/workspace/resources/${res.id}/acknowledgements`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, [res.id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="ack-report-dialog">
        <DialogHeader>
          <DialogTitle>Who has read it</DialogTitle>
          <DialogDescription>{res.title} — version {res.version}. Only people in your team are shown.</DialogDescription>
        </DialogHeader>
        {!data ? <Icons.Loader2 className="mx-auto animate-spin text-[#FFB000]" /> : (
          <div className="max-h-80 space-y-1.5 overflow-y-auto">
            {data.people.map((p) => (
              <div key={p.user_id} className="flex items-center justify-between rounded-lg border border-[#E4E8EF] px-3 py-2 text-sm">
                <span>{p.name}</span>
                {p.acknowledged_at
                  ? <span className="flex items-center gap-1 text-xs text-[#3D8B65]"><Icons.Check size={13} /> {fmtDateTime(p.acknowledged_at)}</span>
                  : <span className="text-xs text-[#C95555]">Outstanding</span>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
